import { useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { isTypingTarget } from '@/lib/keyboard'

interface UseFullscreenShortcutOptions {
  isFullscreen: boolean
  setIsFullscreen: Dispatch<SetStateAction<boolean>>
  canEnter?: boolean
  disabled?: boolean
  lockScroll?: boolean
}

export function useFullscreenShortcut({
  isFullscreen,
  setIsFullscreen,
  canEnter = true,
  disabled = false,
  lockScroll = true,
}: UseFullscreenShortcutOptions): void {
  useEffect(() => {
    if (!isFullscreen || !lockScroll) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isFullscreen, lockScroll])

  useEffect(() => {
    if (disabled) return

    const onKey = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target)) return

      if (event.key === 'Escape') {
        if (!isFullscreen) return
        event.preventDefault()
        setIsFullscreen(false)
        return
      }

      if (event.key.toLowerCase() !== 'f') return
      if (!isFullscreen && !canEnter) return

      event.preventDefault()
      setIsFullscreen((current) => !current)
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canEnter, disabled, isFullscreen, setIsFullscreen])
}
